import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";

import {
  MetricId,
  ScopeLevel,
} from "@transfr-inc/dashboard-components/outcome-metrics";

import { useContainer } from "@src/contexts/container-context";

/**
 * Runaway-cursor backstop for the breakdown infinite query: the most pages it
 * will ever request. A well-behaved server ends paging by omitting
 * `next_page_token`; this ceiling only bounds a cursor that never terminates
 * (keeps returning a fresh token with rows). Set far above any realistic
 * breakdown so it never truncates genuine data.
 */
const BREAKDOWN_MAX_PAGES = 500;

/**
 * React Query hooks for outcome-dashboard data.
 *
 * Two contracts every hook in this file upholds:
 *
 * 1. 403 surfacing (SECURITY): the outcomes proxy rejects out-of-scope /
 *    IDOR-style requests with a 403. These hooks set `throwOnError: false` so
 *    that rejection lands in `query.error` (rather than bubbling to an error
 *    boundary), and {@link normalizeError} guarantees `query.error.status`
 *    holds the HTTP status — so a consumer can branch on
 *    `error?.status === 403` to render an "No data" empty state.
 * 2. Freshness: outcomes are point-in-time and filter-driven, so each query
 *    uses `staleTime: 0` (always refetch on mount) while keeping the global
 *    `gcTime` (10m) so a remount within the window paints cached data first.
 */

export const OUTCOME_QUERY_OPTIONS = {
  staleTime: 0,
  throwOnError: false,
};

/** Matches the TTL core-platform caches its own copy of the catalog for. */
const CATALOG_STALE_TIME_MS = 60 * 60 * 1000;

/**
 * Normalizes a rejected request into an error whose `status` is the HTTP
 * status code, so consumers can rely on `error.status` regardless of how the
 * underlying client shaped the rejection.
 *
 * `HttpClient` rejects with the Axios *response* object (which already carries
 * `.status`); a transport-level failure rejects with the Axios error (status
 * lives under `.response.status`). This collapses both to a single `.status`.
 *
 * The rejected object is never mutated: assigning onto it could throw on a
 * frozen/getter-only rejection and mask the real error. When a status must be
 * derived, a shallow copy carrying `.status` is returned instead; otherwise the
 * original is returned untouched (network/no-status case keeps `.status`
 * absent).
 *
 * @param {unknown} error
 * @returns {unknown} The error (or a shallow copy) with `.status` ensured when
 *   derivable.
 */
function normalizeError(error) {
  if (error && typeof error === "object" && error.status == null) {
    const derived = error.response?.status;
    if (derived != null) {
      return { ...error, status: derived };
    }
  }
  return error;
}

/**
 * Stable hash of an outcomes filter envelope for use in query keys. Stable
 * means key-order independent: the same logical filters always produce the
 * same string regardless of property insertion order, so identical filter sets
 * share a cache entry. ID allowlists are order-normalized for the same reason.
 *
 * Mirrors the wire envelope {@link OutcomesService#encodeFilters} serializes:
 * the date range is the flat `startDate` / `endDate` pair, so a date change
 * rekeys the query rather than serving a stale cache entry.
 *
 * @param {object} [filters]
 * @param {Date|string} [filters.startDate] - Inclusive range start
 * @param {Date|string} [filters.endDate] - Inclusive range end
 * @param {string[]} [filters.orgCodes] - Org code allowlist
 * @param {string[]} [filters.classroomIds] - Classroom id allowlist
 * @param {string[]} [filters.userIds] - Learner (user) id allowlist
 * @param {number[]} [filters.productIds] - Product id allowlist (`Product` ints)
 * @param {string[]} [filters.moduleIds] - Module id allowlist
 * @param {string[]} [filters.programIds] - Program (course) id allowlist
 * @returns {string} Stable hash
 */
export function filtersHash(filters = {}) {
  const sortedIds = (ids) =>
    Array.isArray(ids) ? [...ids].map(String).sort() : [];

  const canonical = {
    startDate: filters.startDate == null ? null : String(filters.startDate),
    endDate: filters.endDate == null ? null : String(filters.endDate),
    orgCodes: sortedIds(filters.orgCodes),
    classroomIds: sortedIds(filters.classroomIds),
    userIds: sortedIds(filters.userIds),
    productIds: sortedIds(filters.productIds),
    moduleIds: sortedIds(filters.moduleIds),
    programIds: sortedIds(filters.programIds),
  };

  return JSON.stringify(canonical);
}

/**
 * Dashboard summary KPIs for the viewer's scope and period.
 *
 * `metrics` narrows the request to a subset of metric keys and is part of the
 * query key, so a narrowed request never shares a cache entry with the full
 * summary. Required alongside a module-scoped filter envelope (see
 * {@link OutcomesService#getDashboardSummary}).
 */
export function useDashboardSummary({ filters, metrics } = {}, options = {}) {
  const { outcomesService } = useContainer();

  return useQuery({
    queryKey: [
      "outcome-summary",
      filtersHash(filters),
      Array.isArray(metrics) ? [...metrics].sort() : null,
    ],
    queryFn: ({ signal }) =>
      outcomesService
        .getDashboardSummary({ filters, metrics, signal })
        .catch((error) => {
          throw normalizeError(error);
        }),
    ...OUTCOME_QUERY_OPTIONS,
    ...options,
  });
}

/**
 * The data service's metric vocabulary, which the metric registry is built from.
 *
 * Unlike every other query here, this is not point-in-time data: the vocabulary
 * changes only when the data team edits its registry, so it is cached rather
 * than refetched on mount.
 *
 * `throwOnError: false` keeps a failed read in `query.error`, so the caller can
 * render the outcomes surface's unavailable state — there is no local fallback
 * vocabulary.
 */
export function useMetricCatalog(options = {}) {
  const { outcomesService } = useContainer();

  return useQuery({
    queryKey: ["outcome-metric-catalog"],
    queryFn: ({ signal }) =>
      outcomesService.getMetricCatalog({ signal }).catch((error) => {
        throw normalizeError(error);
      }),
    staleTime: CATALOG_STALE_TIME_MS,
    gcTime: CATALOG_STALE_TIME_MS,
    throwOnError: false,
    ...options,
  });
}

/**
 * Normalizes the org module catalog (the insights `module/simulations`
 * response) to the `{ id, label }` options the module selector renders. A row's
 * `moduleId` doubles as the `module_id` value sent back to the outcomes proxy
 * to narrow a module-aware metric.
 *
 * @param {any} [modules] - Raw module rows from `getModulesForOrg`. Typed
 *   loosely because the query layer hands the untyped service response straight
 *   to this `select`.
 * @returns {{ id: string, label: string }[]}
 */
export function toModuleOptions(modules = []) {
  return modules
    .map(
      (
        /** @type {{ moduleId?: string|number, displayName?: string }} */ module
      ) => {
        const id = module?.moduleId;
        if (id == null) {
          return null;
        }
        return { id: String(id), label: String(module?.displayName ?? id) };
      }
    )
    .filter(Boolean);
}

/**
 * Module catalog for the per-metric module selector, sourced from the org's
 * module list (insights `getModulesForOrg`) and normalized to `{ id, label }`
 * via {@link toModuleOptions}.
 *
 * @param {Object} [scope]
 * @param {string} [scope.orgCode] - Org whose module catalog to fetch; the
 *   request stays disabled until it is set.
 * @param {boolean} [scope.isParentOrg] - Read the multi-org (parent) catalog.
 * @param {string} [scope.classroomId] - Narrow the catalog to one classroom.
 * @param {Object} [options] - Extra react-query options (merged last).
 */
export function useOutcomeModules(
  { orgCode, isParentOrg = false, classroomId } = {},
  options = {}
) {
  const { insightsService } = useContainer();

  return useQuery({
    queryKey: [
      "outcome-modules",
      orgCode ?? null,
      isParentOrg,
      classroomId ?? null,
    ],
    queryFn: () =>
      insightsService
        .getModulesForOrg(orgCode, isParentOrg, classroomId)
        .catch((error) => {
          throw normalizeError(error);
        }),
    select: toModuleOptions,
    enabled: Boolean(orgCode),
    ...OUTCOME_QUERY_OPTIONS,
    ...options,
  });
}

/**
 * The per-grain breakdown method each ranked-career metric routes to. Keyed by
 * metric id rather than branched on, so a further ranked metric is a row here
 * and nothing else. Every id `isRankedCareerBreakdown` names needs an entry.
 *
 * @type {Record<string, { org: string, classroom: string, learner: string }>}
 */
const RANKED_BREAKDOWN_METHODS = {
  [MetricId.TopCareers]: {
    org: "getTopCareersOrgBreakdown",
    classroom: "getTopCareersClassroomBreakdown",
    learner: "getTopCareersLearnerBreakdown",
  },
  [MetricId.TopCareerPlans]: {
    org: "getTopCareerPlansOrgBreakdown",
    classroom: "getTopCareerPlansClassroomBreakdown",
    learner: "getTopCareerPlansLearnerBreakdown",
  },
  [MetricId.TopClusters]: {
    org: "getTopClustersOrgBreakdown",
    classroom: "getTopClustersClassroomBreakdown",
    learner: "getTopClustersLearnerBreakdown",
  },
};

/**
 * One breakdown page for a (metric, level) pair, from the endpoint the metric
 * routes to: a ranked-career metric ({@link RANKED_BREAKDOWN_METHODS}) has its own
 * per-grain path, every other metric goes through the generic breakdown.
 *
 * @param {object} outcomesService
 * @param {object} request
 * @param {import("@transfr-inc/dashboard-components/outcome-metrics").HierarchyLevel} request.level
 * @param {string} [request.parentId] - Org code (classroom grain) or classroom
 *   id (learner grain) whose children are broken down.
 * @param {string} [request.metric]
 * @param {object} [request.filters]
 * @param {string} [request.orgCode] - Org owning `parentId` at the learner grain.
 * @param {string} [request.sort] - Wire ordering (comma-separated `field:dir`
 *   tokens).
 * @param {boolean} [request.includeAnonymous] - Learner grain only: `false`
 *   hides anonymous learners.
 * @param {boolean} [request.includeEmpty] - `true` keeps entities with no data
 *   for the metric.
 * @param {string} [request.pageToken]
 * @param {AbortSignal} [request.signal]
 * @returns {Promise<{ data: Array<object>, total_rows: number, next_page_token: string|null }>}
 */
export function fetchBreakdown(
  outcomesService,
  {
    level,
    parentId,
    metric,
    filters,
    orgCode,
    sort,
    includeAnonymous,
    includeEmpty,
    pageToken,
    signal,
  }
) {
  const ranked = RANKED_BREAKDOWN_METHODS[metric];
  if (ranked) {
    if (level === ScopeLevel.Org) {
      return outcomesService[ranked.org]({
        filters,
        sort,
        includeEmpty,
        pageToken,
        signal,
      });
    }
    if (level === ScopeLevel.Classroom) {
      return outcomesService[ranked.classroom]({
        orgId: parentId,
        filters,
        sort,
        includeEmpty,
        pageToken,
        signal,
      });
    }
    return outcomesService[ranked.learner]({
      orgId: orgCode,
      classroomId: parentId,
      filters,
      sort,
      includeEmpty,
      includeAnonymous,
      pageToken,
      signal,
    });
  }

  if (level === ScopeLevel.Org) {
    return outcomesService.getOrgBreakdown({
      metric,
      filters,
      sort,
      includeEmpty,
      pageToken,
      signal,
    });
  }
  if (level === ScopeLevel.Classroom) {
    return outcomesService.getClassroomBreakdown({
      orgId: parentId,
      metric,
      filters,
      sort,
      includeEmpty,
      pageToken,
      signal,
    });
  }
  return outcomesService.getLearnerBreakdown({
    orgId: orgCode,
    classroomId: parentId,
    metric,
    filters,
    sort,
    includeEmpty,
    includeAnonymous,
    pageToken,
    signal,
  });
}

/**
 * Per-level metric breakdown across the children of a parent scope. `level` is
 * the level of the *rows* returned, which selects the proxy endpoint:
 * - `org` → by_organization (no parent; the viewer's subtree)
 * - `classroom` → by_classroom within `parentId` (an org code)
 * - `learner` → by_learner within `parentId` (a classroom id)
 *
 * The by_learner endpoint keys off both its classroom (`parentId`) and that
 * classroom's org, so `orgCode` is required at the learner level (and ignored
 * at every other level, where the parent alone identifies the scope).
 *
 * `metric` is the single metric each row is broken down for; the response rows
 * carry its total and a backend-defined series split. A ranked-career metric
 * ({@link RANKED_BREAKDOWN_METHODS}) instead names one career per row.
 *
 * `sort` is the endpoints' wire ordering (comma-separated `field:dir` tokens).
 * It keys the query, so a changed order starts a fresh page set rather than
 * appending differently-ordered pages to the ones on hand.
 *
 * Paged as an infinite query: the endpoints return rows already ordered, so each
 * page is a contiguous slice of that order and the caller appends pages via
 * `fetchNextPage` as the viewer scrolls, rather than loading every row up front.
 * The result's `data.pages` is the array of page envelopes
 * (`{ data, total_rows, next_page_token }`), newest last.
 *
 * {@link getBreakdownNextPageParam} guards `getNextPageParam` against a
 * misbehaving cursor (empty page, repeated token, runaway page count) so a
 * non-terminating server can never loop `fetchNextPage` forever.
 */
export function useOutcomeBreakdown(
  {
    level,
    parentId,
    metric,
    filters,
    orgCode,
    sort,
    includeAnonymous = true,
    includeEmpty = false,
  },
  options = {}
) {
  const { outcomesService } = useContainer();

  return useInfiniteQuery({
    queryKey: [
      "outcome-breakdown",
      level,
      parentId ?? null,
      metric ?? null,
      sort ?? null,
      level === ScopeLevel.Learner ? includeAnonymous : true,
      includeEmpty,
      filtersHash(filters),
    ],
    queryFn: ({ pageParam, signal }) =>
      fetchBreakdown(outcomesService, {
        level,
        parentId,
        metric,
        filters,
        orgCode,
        sort,
        includeAnonymous,
        includeEmpty,
        pageToken: pageParam,
        signal,
      }).catch((error) => {
        throw normalizeError(error);
      }),
    initialPageParam: undefined,
    getNextPageParam: getBreakdownNextPageParam,
    ...OUTCOME_QUERY_OPTIONS,
    ...options,
    // `org` breaks down the viewer's whole subtree, so it needs no parentId;
    // classroom/learner drill into a specific parent. The learner endpoint also
    // needs the parent classroom's `orgCode` in its path, so gate on it too.
    enabled:
      (level === ScopeLevel.Org || !!parentId) &&
      (level !== ScopeLevel.Learner || !!orgCode) &&
      !!metric &&
      (options.enabled ?? true),
  });
}

/**
 * `getNextPageParam` for the breakdown infinite query: the cursor for the next
 * page, or `undefined` when paging should stop.
 *
 * Stops on every way a cursor can fail to make forward progress, so a
 * misbehaving server can never drive an unbounded fetch loop: the server omits
 * a token (the normal end), the last page held no rows, the token repeats the
 * one that fetched the last page, or the page count hits
 * {@link BREAKDOWN_MAX_PAGES}.
 *
 * @param {{ data: Array<object>, next_page_token: string|null }} lastPage
 * @param {Array<object>} allPages
 * @param {string|undefined} lastPageParam - The token that fetched `lastPage`.
 * @returns {string|undefined}
 */
export function getBreakdownNextPageParam(lastPage, allPages, lastPageParam) {
  const token = lastPage?.next_page_token || undefined;
  if (
    !token ||
    lastPage.data.length === 0 ||
    token === lastPageParam ||
    allPages.length >= BREAKDOWN_MAX_PAGES
  ) {
    return;
  }
  return token;
}

/**
 * Metric value over time for the viewer's scope.
 */
export function useOutcomeTimeSeries(
  { metric, granularity, breakdown, filters },
  options = {}
) {
  const { outcomesService } = useContainer();

  return useQuery({
    queryKey: [
      "outcome-time-series",
      metric,
      granularity ?? null,
      breakdown ?? null,
      filtersHash(filters),
    ],
    queryFn: ({ signal }) =>
      outcomesService
        .getTimeSeries({ metric, granularity, breakdown, filters, signal })
        .catch((error) => {
          throw normalizeError(error);
        }),
    ...OUTCOME_QUERY_OPTIONS,
    ...options,
    enabled: !!metric && (options.enabled ?? true),
  });
}

/**
 * Careers explored in the viewer's scope, ranked by rating, with the per-career
 * rating split. Resolves to `{ data, total_rows, next_page_token }`, where
 * `data` holds the whole ranked list — the service follows the cursor before
 * resolving. The query carries no placeholder, so gauge readiness from
 * `isLoading`.
 *
 * @param {{ filters?: object, includeAnonymous?: boolean }} [request]
 */
export function useTopCareers(
  { filters, includeAnonymous = true } = {},
  options = {}
) {
  const { outcomesService } = useContainer();

  return useQuery({
    queryKey: ["outcome-top-careers", filtersHash(filters), includeAnonymous],
    queryFn: ({ signal }) =>
      outcomesService
        .getTopCareers({ filters, includeAnonymous, signal })
        .catch((error) => {
          throw normalizeError(error);
        }),
    ...OUTCOME_QUERY_OPTIONS,
    ...options,
  });
}

/**
 * Careers chosen in submitted Trek Mobile career plans, ranked by plan count.
 * The Trek Mobile sibling of {@link useTopCareers}, resolving the same
 * `{ data, total_rows, next_page_token }` envelope with the whole ranked list in
 * `data`.
 *
 * @param {{ filters?: object, includeAnonymous?: boolean }} [request]
 */
export function useTopCareerPlans(
  { filters, includeAnonymous = true } = {},
  options = {}
) {
  const { outcomesService } = useContainer();

  return useQuery({
    queryKey: [
      "outcome-top-career-plans",
      filtersHash(filters),
      includeAnonymous,
    ],
    queryFn: ({ signal }) =>
      outcomesService
        .getTopCareerPlans({ filters, includeAnonymous, signal })
        .catch((error) => {
          throw normalizeError(error);
        }),
    ...OUTCOME_QUERY_OPTIONS,
    ...options,
  });
}

/**
 * Career clusters explored in the viewer's scope, ranked by 4-5 star ratings.
 * The cluster sibling of {@link useTopCareers}, resolving the same
 * `{ data, total_rows, next_page_token }` envelope with the whole ranked list in
 * `data`.
 *
 * @param {{ filters?: object, includeAnonymous?: boolean }} [request]
 */
export function useTopClusters(
  { filters, includeAnonymous = true } = {},
  options = {}
) {
  const { outcomesService } = useContainer();

  return useQuery({
    queryKey: ["outcome-top-clusters", filtersHash(filters), includeAnonymous],
    queryFn: ({ signal }) =>
      outcomesService
        .getTopClusters({ filters, includeAnonymous, signal })
        .catch((error) => {
          throw normalizeError(error);
        }),
    ...OUTCOME_QUERY_OPTIONS,
    ...options,
  });
}

/**
 * How often, in ms, {@link useExportStatus} re-polls a running export job.
 */
const EXPORT_POLL_INTERVAL_MS = 15_000;

/**
 * Terminal export-job states. Polling stops once the job reaches one of these.
 */
const EXPORT_TERMINAL_STATES = new Set(["ready", "failed"]);

/**
 * `refetchInterval` for {@link useExportStatus}: keep polling every
 * {@link EXPORT_POLL_INTERVAL_MS} while a job is in flight, and stop (return
 * `false`) once it reaches a terminal status.
 *
 * @param {{ state: { data?: { status?: string } } }} query
 * @returns {number|false}
 */
export function exportStatusRefetchInterval(query) {
  return EXPORT_TERMINAL_STATES.has(query.state.data?.status)
    ? false
    : EXPORT_POLL_INTERVAL_MS;
}

/**
 * Starts a CSV export job. Returns a mutation whose `mutate({ level, orgCode,
 * filters, metricIds, format, idempotencyKey })` resolves to `{ job_id }`.
 *
 * A rejection is normalized so `error.status` and the backend `error.data`
 * detail (e.g. the metric-empty or per-simulation 400) are reachable by the
 * caller's `onError`.
 */
export function useStartCsvExport(options = {}) {
  const { outcomesService } = useContainer();

  return useMutation({
    mutationFn: (/** @type {object} */ variables) =>
      outcomesService.startCsvExport(variables).catch((error) => {
        throw normalizeError(error);
      }),
    ...options,
  });
}

/**
 * Polls a CSV export job's status until it is `ready` or `failed`.
 *
 * The query is enabled only when a `jobId` exists, so "no job yet" reads as "not
 * started" rather than a pending fetch — a consumer gauges busy state from the
 * enabled query's `isFetching`/`isLoading`, not `isPlaceholderData` (there is no
 * placeholder). `refetchInterval` re-polls every
 * {@link EXPORT_POLL_INTERVAL_MS} until a terminal status lands, then stops.
 *
 * @param {string|undefined|null} jobId
 * @param {object} [options]
 */
export function useExportStatus(jobId, options = {}) {
  const { outcomesService } = useContainer();

  return useQuery({
    queryKey: ["outcome-export-status", jobId ?? null],
    queryFn: ({ signal }) =>
      outcomesService.getExportStatus(jobId, { signal }).catch((error) => {
        throw normalizeError(error);
      }),
    ...OUTCOME_QUERY_OPTIONS,
    ...options,
    enabled: !!jobId && (options.enabled ?? true),
    refetchInterval: exportStatusRefetchInterval,
  });
}

/**
 * Awards earned, totaled by award type, with an optional per-classroom split.
 */
export function useAwardsBreakdown({ breakdown, filters } = {}, options = {}) {
  const { outcomesService } = useContainer();

  return useQuery({
    queryKey: ["outcome-awards", breakdown ?? null, filtersHash(filters)],
    queryFn: ({ signal }) =>
      outcomesService
        .getAwardsBreakdown({ breakdown, filters, signal })
        .catch((error) => {
          throw normalizeError(error);
        }),
    ...OUTCOME_QUERY_OPTIONS,
    ...options,
  });
}
